import { HeadcountLogin } from "@src/pages/headcount-tracking/HeadcountLogin";
import HrSyncLoading from "@src/pages/headcount-tracking/HrSyncLoading";
import { useHrSyncStep } from "@src/pages/headcount-tracking/useHrSyncStep";
import { HrSyncStep } from "@src/__generated__/graphql";
import React, { useMemo } from "react";
import { HrDepartmentsManager } from "@src/pages/headcount-tracking/mapping/HrDepartmentsManager";
import { HrLocationsManager } from "@src/pages/headcount-tracking/mapping/HrLocationsManager";
import { getPageTitle } from "@src/utils/page";
import {
  Header,
  PageLayout,
  PageRootWithNoPad,
  Sidebar,
} from "@src/components/modules";
import { Page } from "@src/shared/pages";
import { SegmentedStepBar } from "@src/components/modules/SegmentedStepBar/SegmentedStepBar";

export function HeadcountGatekeeper({
  children,
}: {
  children: React.ReactNode;
}) {
  const { step, startPolling, stopPolling, refetch } = useHrSyncStep();

  const main = useMemo(() => {
    if (!step) return;

    switch (step) {
      case HrSyncStep.LOGIN:
        return <HeadcountLogin />;
      case HrSyncStep.SYNCING:
        startPolling(5000);
        return <HrSyncLoading />;
      case HrSyncStep.MAPPING_DEPARTMENTS:
        stopPolling();
        return (
          <HrDepartmentsManager
            shouldSplitByUnmapped={false}
            onSave={refetch}
          />
        );
      case HrSyncStep.MAPPING_LOCATIONS:
        stopPolling();
        return (
          <HrLocationsManager shouldSplitByUnmapped={false} onSave={refetch} />
        );
      case HrSyncStep.COMPLETE:
        stopPolling();
        return children;
      default:
        stopPolling();
        return children;
    }
  }, [step, startPolling, stopPolling, refetch, children]);

  const title =
    step === HrSyncStep.LOGIN
      ? "New Headcount Integration - Login"
      : step === HrSyncStep.SYNCING
      ? "New Headcount Integration - Syncing"
      : step === HrSyncStep.MAPPING_DEPARTMENTS
      ? "New Headcount Integration - Mapping Departments"
      : step === HrSyncStep.MAPPING_LOCATIONS
      ? "New Headcount Integration - Mapping Locations"
      : "Headcount - Roster";

  const steps = useMemo(() => {
    if (!step) return [];

    return [
      {
        title: "Login",
        index: 0,
        active: step === HrSyncStep.LOGIN,
      },
      {
        title: "Syncing",
        index: 1,
        active: step === HrSyncStep.SYNCING,
      },
      {
        title: "Mapping Departments",
        index: 2,
        active: step === HrSyncStep.MAPPING_DEPARTMENTS,
      },
      {
        title: "Mapping Locations",
        index: 3,
        active: step === HrSyncStep.MAPPING_LOCATIONS,
      },
    ];
  }, [step]);

  return (
    <PageLayout
      title={getPageTitle(title)}
      header={<Header pageTitle={title}></Header>}
      sidebar={<Sidebar activePage={Page.HEADCOUNT_ROSTER} />}
      main={
        <PageRootWithNoPad>
          {step !== HrSyncStep.COMPLETE ? (
            <SegmentedStepBar steps={steps} />
          ) : (
            <></>
          )}{" "}
          {main}
        </PageRootWithNoPad>
      }
    />
  );
}

export default HeadcountGatekeeper;
